<script setup lang="ts">
import { useSearchInput } from '@base/composables/searchInput'
import { defineShortcuts } from '#ui/composables/defineShortcuts'
import { ut_getText } from '@base/utils/utils'

const p = defineProps<{
  isInternal?: boolean
  isHistoric?: boolean
  isSettings?: boolean
  realCountry?: string
  useAdesso?: boolean
}>()

const cp = useSearchInput()
const open: Ref<boolean> = ref(false)
const openHistoric: Ref<boolean> = ref(false)
const searchInput: Ref<string> = ref('')
const suggestions: Ref<string[]> = ref([])
const showSuggestion: Ref<boolean> = ref(false)

defineShortcuts({
  enter: {
    usingInput: 'search', // only fire when THIS search field is focused — not any input on the page
    handler: () => !p.useAdesso ? cp.doSearch(cp.searchInput.value) : doSearchAdesso(searchInput.value),
  },
})

watch(() => cp.suggestions.value, () => {
  open.value = cp.suggestions.value.length > 0
})

watch(() => suggestions.value, () => {
  openHistoric.value = suggestions.value.length > 0
})

async function getElasticSearchSuggestions(): Promise<void> {
  showSuggestion.value = false
  suggestions.value = []
  if (searchInput.value.length > 1) {
    const url: string = 'https://wilo.com/search/amirada_live_' + p.realCountry + '-' + cp.indexStore.state.paramData.lcda + '/_search'
    const must: any = []
    const mustTemp: any = []
    let mustTerm: any

    // Historic or no Motor
    if (p.isHistoric) {
    // only historic
      mustTerm = {
        term: {
          isHistoric: true,
        },
      }
    }
    else {
    // noMotors
      mustTerm = {
        term: {
          isMotor: false,
        },
      }
    }
    mustTemp.push(mustTerm)

    // onlyFullSearch
    mustTerm = {
      term: {
        onlyFullSearch: false,
      },
    }
    mustTemp.push(mustTerm)

    // validTo
    mustTerm = {
      range: {
        validTo: {
          format: 'yyyy/MM/dd',
          gte: 'now/d',
        },
      },
    }
    mustTemp.push(mustTerm)

    // validFrom
    mustTerm = {
      range: {
        validFrom: {
          format: 'yyyy/MM/dd',
          lte: 'now/d',
        },
      },
    }
    mustTemp.push(mustTerm)

    const mustBool: any = {
      bool: {
        must: mustTemp,
      },
    }
    must.push(mustBool)

    // search term only in desc
    const searchDesc: any = {
      match_phrase_prefix: {
        desc: {
          max_expansions: 25,
          query: searchInput.value,
        },
      },
    }
    must.push(searchDesc)

    const post: any = {
      query: {
        bool: {
          must,
        },
      },
    }

    let res: any
    try {
      res = await $fetch(url, { method: 'POST', body: post })
      if (res['hits']['hits']) {
        const hits: any = res['hits']['hits']
        for (const hit of hits) {
          const desc: string = hit['_source']['desc']
          if (!suggestions.value.includes(desc)) {
            suggestions.value.push(desc)
          }
        }
      }
    }
    catch (e) {
      suggestions.value = []
    }
    showSuggestion.value = suggestions.value.length > 0
  }
}

function doSearchAdesso(suggestion: string): void {
  const path: string = cp.indexStore.state.paramData.embeddedInfo.embparam4 ? cp.indexStore.state.paramData.embeddedInfo.embparam4 : 'https://wilo.com/' + p.realCountry + '/' + cp.indexStore.state.paramData.lcda + '/Search-results.html'
  const query: string = 'searchType=products&count=20&sortBy=relevance&sortOrder=desc&query=' + suggestion + (p.isHistoric ? '&filters=historic' : '')
  window.location.href = path + '?' + query
}
</script>

<template>
  <UPopover v-if="!useAdesso" v-model:open="open">
    <template #anchor>
      <UInput
        v-model="cp.searchInput.value"
        name="search"
        :size="(!isHistoric || isSettings) ? undefined : 'xl'"
        :class="(!isHistoric || isSettings) ? 'md:w-75' : 'w-full max-w-200'"
        :placeholder="!isInternal ? (!isHistoric || isSettings) ? ut_getText('def_search_txt_field') : ut_getText('label-historic-search-field') : 'Search'"
        @focus="cp.getSearchSuggestions(cp.searchInput.value.toString())"
        @update:model-value="cp.getSearchSuggestions($event.toString())"
      >
        <template #trailing>
          <UButton icon="i-wilo-magnifying-glass-solid-full" variant="ghost" color="tertiary" :disabled="cp.searchInput.value.length === 0" @click="cp.doSearch(cp.searchInput.value)" />
        </template>
      </UInput>
    </template>

    <template #content>
      <div class="p-1">
        <ul>
          <li
            v-for="suggestion in cp.suggestions.value"
            :key="suggestion"
            class="hover:bg-gray-50 p-4 cursor-pointer"
            @click="cp.doSearch(suggestion)"
            v-html="suggestion"
          />
        </ul>
      </div>
    </template>
  </UPopover>

  <UPopover v-if="useAdesso" v-model:open="showSuggestion">
    <template #anchor>
      <UInput
        v-model="searchInput"
        name="search"
        class="w-full max-w-200"
        size="xl"
        :placeholder="ut_getText('label-historic-search-field')"
        @focus="searchInput.length > 2 ? getElasticSearchSuggestions() : ''"
        @update:model-value="searchInput.length > 2 ? getElasticSearchSuggestions() : ''"
      >
        <template #trailing>
          <UButton icon="i-wilo-magnifying-glass-solid-full" variant="ghost" color="tertiary" :disabled="searchInput.length === 0" @click="doSearchAdesso(searchInput)" />
        </template>
      </UInput>
    </template>

    <template #content>
      <div class="p-1">
        <ul>
          <li
            v-for="suggestion in suggestions"
            :key="suggestion"
            class="hover:bg-gray-50 p-4 cursor-pointer"
            @click="doSearchAdesso(suggestion)"
            v-html="suggestion"
          />
        </ul>
      </div>
    </template>
  </UPopover>
</template>
